import {
  Box,
  Typography,
  styled,
  Divider,
  CircularProgress,
} from "@mui/material";
import BannerAndAvatar from "./_components/banner-avatar";
import { PersonalInformation } from "./_components/personal-information";
import { useFormik } from "formik";
import { ChangePassword } from "./_components/change-password";
import {
  changePasswordSchema,
  updateProfileSchema,
} from "@/schemas/profile.schema";
import { useAdminUser } from "@/hooks/use-admin-user";
import { formatRole } from "@/utils/format-role";
import { ProfileAccount } from "./_components/profile-account";
import { formatLabel } from "@/utils/format-label";

import { useMutation } from "@tanstack/react-query";
import { changePassword, updateAdminUser } from "@/services/users.service";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";
import { useQueryClient } from "@tanstack/react-query";
import { TwoFactorAuth } from "./_components/two-factor-auth";

const SectionWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
}));

const AdminProfile = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useAdminUser();

  const dispatch = useDispatch();

  const { mutate: updateMutateProfile, isPending: isUpdatingProfile } =
    useMutation({
      mutationFn: updateAdminUser,
      onSuccess: (data) => {
        profileFormik.resetForm();
        dispatch(
          showToast(data?.message ?? "Profile updated successfully", "success"),
        );

        queryClient.invalidateQueries({ queryKey: ["admin-user"] });
      },
      onError: (err) => {
        dispatch(
          showToast(err?.message ?? "Failed to update profile", "error"),
        );
      },
    });

  const profileFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: data?.firstName ?? "",
      lastName: data?.surname ?? "",
      email: data?.email ?? "",
      phoneNumber: data?.phoneNumber ?? "",
      address: data?.address ?? "",
      district: data?.district ?? "",
      city: data?.city ?? "",
      state: data?.state ?? "",
      gender: data?.gender ?? "",
      isActive: data?.active ?? undefined,
    },
    validationSchema: updateProfileSchema,
    onSubmit: (values) => {
      updateMutateProfile({
        ...values,
        surname: values.lastName,
      });
    },
  });

  const { mutate: changePasswordMutate, isPending: isChangingPassword } =
    useMutation({
      mutationFn: changePassword,
      onSuccess: (data) => {
        passwordFormik.resetForm();
        dispatch(
          showToast(
            data?.message ?? "Password changed successfully",
            "success",
          ),
        );
      },
      onError: (err) => {
        dispatch(
          showToast(err?.message ?? "Failed to change password", "error"),
        );
      },
    });

  const passwordFormik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
    },
    validationSchema: changePasswordSchema,
    onSubmit: (values) => {
      changePasswordMutate({
        ...values,
        phoneOrEmail: data?.email,
      });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}>
        <CircularProgress size={16} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!data) return null;

  const rolesText = data?.roles?.map(formatRole).join(", ") || "";

  return (
    <Box>
      <BannerAndAvatar
        firstName={formatLabel(data?.firstName)}
        lastName={formatLabel(data?.surname)}
        role={rolesText || formatLabel(data?.customerType)}
        profileImage={null}
      />
      <SectionWrapper>
        <Typography variant="h6" fontWeight={500}>
          Admin Profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update your admin information here.
        </Typography>
      </SectionWrapper>
      <Divider />
      <SectionWrapper>
        <form onSubmit={profileFormik.handleSubmit}>
          <PersonalInformation
            formik={profileFormik}
            isDisabled={isUpdatingProfile}
          />
        </form>
      </SectionWrapper>
      <Divider />
      <SectionWrapper>
        <ProfileAccount data={data} />
      </SectionWrapper>
      <Divider />
      <SectionWrapper>
        <form onSubmit={passwordFormik.handleSubmit}>
          <ChangePassword
            formik={passwordFormik}
            isDisabled={isChangingPassword}
          />
        </form>
      </SectionWrapper>
      <Divider />
      <SectionWrapper>
        <TwoFactorAuth data={data} />
      </SectionWrapper>
    </Box>
  );
};

export default AdminProfile;
